import { is } from 'bpmn-js/lib/util/ModelUtil';
import { isExpanded } from 'bpmn-js/lib/util/DiUtil';

const CONNECTION_TYPES = new Set([
  'bpmn:SequenceFlow',
  'bpmn:MessageFlow',
  'bpmn:Association'
]);

/**
 * Adds "In Sub-Prozess umwandeln" entries to the replace (wrench) popup menu
 * when multiple elements are selected.  The user can choose between an
 * expanded (visible children) or collapsed (drill-down plane) sub-process.
 *
 * Also listens for the custom event `wrapInSubProcess.trigger` so that the
 * React layer (dialog, keyboard shortcut) can invoke the operation.
 */
export default function WrapInSubProcessProvider(injector) {
  this._injector = injector;

  const popupMenu = injector.get('popupMenu');
  popupMenu.registerProvider('bpmn-replace', 600, this);

  const eventBus = injector.get('eventBus');

  // React bridge: the dialog fires this event with { name, collapsed }
  eventBus.on('wrapInSubProcess.trigger', (event) => {
    const selection = injector.get('selection');
    const elements = selection.get();
    if (elements.length < 2) return;

    this._doWrap(elements, event.name || 'Sub-Prozess', event.collapsed);
  });
}

WrapInSubProcessProvider.$inject = ['injector'];


// ---------------------------------------------------------------------------
// Popup menu integration
// ---------------------------------------------------------------------------

WrapInSubProcessProvider.prototype.getPopupMenuEntries = function (target) {
  const injector = this._injector;
  const translate = injector.get('translate');
  const selection = injector.get('selection');

  return (entries) => {
    const selectedElements = selection.get();

    // Only show when ≥ 2 elements are selected and the target is among them
    if (selectedElements.length < 2) return entries;
    if (!selectedElements.includes(target)) return entries;

    // All selected elements must be flow nodes or connections on the same plane
    if (!allOnSamePlane(selectedElements)) return entries;

    const entryExpanded = {
      label: translate('In Sub-Prozess umwandeln (aufgeklappt)'),
      className: 'bpmn-icon-subprocess-expanded',
      action: () => this._doWrap(selectedElements, '', true)
    };

    const entryCollapsed = {
      label: translate('In Sub-Prozess umwandeln (zugeklappt)'),
      className: 'bpmn-icon-subprocess-collapsed',
      action: () => this._doWrap(selectedElements, '', false)
    };

    // Append at the end
    entries['wrap-in-expanded-subprocess'] = entryExpanded;
    entries['wrap-in-collapsed-subprocess'] = entryCollapsed;
    return entries;
  };
};


// ---------------------------------------------------------------------------
// Core wrap logic
// ---------------------------------------------------------------------------

/**
 * Wrap the given elements in a new sub-process (expanded or collapsed).
 *
 * High-level flow:
 * 1. Compute bounding box of selected shapes
 * 2. Identify internal connections (both ends inside) and boundary connections
 * 3. Create the sub-process shape
 * 4. Move elements into it (expanded) or transfer to a new plane (collapsed)
 * 5. Rewire boundary connections to the new sub-process shape
 */
WrapInSubProcessProvider.prototype._doWrap = function (elements, name, collapsed) {
  const injector = this._injector;
  const eventBus = injector.get('eventBus');

  // Separate shapes from connections
  const shapes = [];
  for (const el of elements) {
    if (!el.waypoints && !CONNECTION_TYPES.has(el.type)) {
      shapes.push(el);
    }
  }

  if (shapes.length === 0) return;

  // Selected shape IDs for quick lookups
  const selectedIds = new Set(elements.map(e => e.id));

  // Find the parent (root or sub-process) — all elements should share the same parent
  const parent = shapes[0].parent;

  // Compute bounding box of shapes
  const bbox = computeBoundingBox(shapes);
  const PADDING = 40;

  // Classify connections
  const { internal, incoming: boundaryIncoming, outgoing: boundaryOutgoing } =
    classifyConnections(shapes, selectedIds);

  if (collapsed) {
    this._wrapCollapsed({ shapes, internal, boundaryIncoming, boundaryOutgoing,
      parent, bbox, name, padding: PADDING });
  } else {
    this._wrapExpanded({ shapes, internal, boundaryIncoming, boundaryOutgoing,
      parent, bbox, name, padding: PADDING });
  }

  eventBus.fire('elements.changed', { elements: [parent] });
};


// ---------------------------------------------------------------------------
// Expanded wrap: move elements into a visible sub-process container
// ---------------------------------------------------------------------------

WrapInSubProcessProvider.prototype._wrapExpanded = function (opts) {
  const { shapes, boundaryIncoming, boundaryOutgoing, parent, bbox, name, padding } = opts;
  const injector = this._injector;
  const modeling = injector.get('modeling');
  const elementFactory = injector.get('elementFactory');
  const selection = injector.get('selection');
  const elementRegistry = injector.get('elementRegistry');

  // 1. Record boundary connection info BEFORE any mutations
  //    incoming: external source → internal target
  //    outgoing: internal source → external target
  const incomingInfo = boundaryIncoming.map(c => ({
    sourceId: c.source.id,
    targetId: c.target.id
  }));
  const outgoingInfo = boundaryOutgoing.map(c => ({
    sourceId: c.source.id,
    targetId: c.target.id
  }));

  // Unique entry points (internal shapes that receive flows from outside)
  const entryTargetIds = [...new Set(incomingInfo.map(i => i.targetId))];
  // Unique exit points (internal shapes that send flows to outside)
  const exitSourceIds = [...new Set(outgoingInfo.map(i => i.sourceId))];
  // Unique external predecessors (what connected INTO the selection)
  const externalSourceIds = [...new Set(incomingInfo.map(i => i.sourceId))];
  // Unique external successors (what the selection connected TO)
  const externalTargetIds = [...new Set(outgoingInfo.map(i => i.targetId))];

  // 2. Remove boundary connections (they'll be replaced)
  const boundaryToRemove = [...boundaryIncoming, ...boundaryOutgoing];
  if (boundaryToRemove.length > 0) {
    modeling.removeElements(boundaryToRemove);
  }

  // 3. Create expanded sub-process
  const EVENT_MARGIN = 60;
  const subProcessWidth = bbox.width + padding * 2 + EVENT_MARGIN * 2;
  const subProcessHeight = bbox.height + padding * 2;

  const subProcessShape = elementFactory.createShape({
    type: 'bpmn:SubProcess',
    isExpanded: true,
    width: subProcessWidth,
    height: subProcessHeight
  });

  const centerX = bbox.x + bbox.width / 2;
  const centerY = bbox.y + bbox.height / 2;

  const newShape = modeling.createShape(subProcessShape, { x: centerX, y: centerY }, parent);

  if (name) {
    modeling.updateProperties(newShape, { name });
  }

  // 4. Move selected shapes into the sub-process
  modeling.moveElements(shapes, { x: 0, y: 0 }, newShape);

  // 5. Create Start Event inside sub-process (left side)
  const startEventShape = elementFactory.createShape({ type: 'bpmn:StartEvent' });
  const startEvent = modeling.createShape(
    startEventShape, { x: newShape.x + 30, y: centerY }, newShape
  );

  // 6. Create End Event inside sub-process (right side)
  const endEventShape = elementFactory.createShape({ type: 'bpmn:EndEvent' });
  const endEvent = modeling.createShape(
    endEventShape, { x: newShape.x + newShape.width - 30, y: centerY }, newShape
  );

  // 7. Connect Start Event → entry shapes (those that had incoming from outside)
  connectStartEvent(entryTargetIds, shapes, startEvent, modeling, elementRegistry);

  // 8. Connect exit shapes → End Event (those that had outgoing to outside)
  connectEndEvent(exitSourceIds, shapes, endEvent, modeling, elementRegistry);

  // 9. Connect external predecessors → sub-process shape
  connectByIds(externalSourceIds, newShape, 'source', modeling, elementRegistry);

  // 10. Connect sub-process shape → external successors
  connectByIds(externalTargetIds, newShape, 'target', modeling, elementRegistry);

  selection.select(newShape);
};


// ---------------------------------------------------------------------------
// Collapsed wrap: create plane, transfer elements, replace with collapsed shape
// ---------------------------------------------------------------------------

WrapInSubProcessProvider.prototype._wrapCollapsed = function (opts) {
  const { shapes, internal, boundaryIncoming, boundaryOutgoing, parent, bbox, name, padding } = opts;
  const injector = this._injector;
  const modeling = injector.get('modeling');
  const elementFactory = injector.get('elementFactory');
  const canvas = injector.get('canvas');
  const bpmnFactory = injector.get('bpmnFactory');
  const selection = injector.get('selection');
  const eventBus = injector.get('eventBus');

  // 1. Create collapsed sub-process shape at center of bounding box
  const centerX = bbox.x + bbox.width / 2;
  const centerY = bbox.y + bbox.height / 2;

  const subProcessShape = elementFactory.createShape({
    type: 'bpmn:SubProcess',
    isExpanded: false
  });

  const newShape = modeling.createShape(subProcessShape, { x: centerX, y: centerY }, parent);

  if (name) {
    modeling.updateProperties(newShape, { name });
  }

  // 2. Find the auto-created plane (from SubProcessPlaneBehavior)
  const planeId = newShape.id + '_plane';
  let subProcessPlane = canvas.getRootElements().find(r => r.id === planeId);

  if (!subProcessPlane) {
    // Create plane manually if not auto-generated
    subProcessPlane = ensureSubProcessPlane(newShape, canvas, bpmnFactory, elementFactory);
  }

  if (!subProcessPlane) {
    console.error('[WrapInSubProcess] Could not create sub-process plane');
    return;
  }

  // 3. Move shapes into the sub-process plane
  //    We need to: (a) move the semantic model (BO), (b) move the DI,
  //    (c) transfer visual shapes to the other plane
  const subProcessBO = newShape.businessObject;
  if (!subProcessBO.flowElements) subProcessBO.flowElements = [];

  // Collect all connections that are internal (both source & target in selection)

  // Normalize positions relative to plane (center them)
  const offsetX = bbox.x - padding;
  const offsetY = bbox.y - padding;

  // Switch to the sub-process plane to create shapes there
  const previousRoot = canvas.getRootElement();

  // First, collect all shape data before removing them
  const shapeData = shapes.map(s => ({
    id: s.id,
    type: s.type,
    x: s.x - offsetX,
    y: s.y - offsetY,
    width: s.width,
    height: s.height,
    businessObject: s.businessObject,
    di: s.di,
    isExpanded: is(s, 'bpmn:SubProcess') ? isExpanded(s) : undefined
  }));

  const connectionData = internal.map(c => ({
    id: c.id,
    type: c.type,
    businessObject: c.businessObject,
    di: c.di,
    sourceId: c.source.id,
    targetId: c.target.id,
    waypoints: c.waypoints ? c.waypoints.map(wp => ({
      x: wp.x - offsetX,
      y: wp.y - offsetY
    })) : []
  }));

  // 4. Collect entry/exit info BEFORE any mutations
  const entryTargetIds = [...new Set(boundaryIncoming.map(c => c.target.id))];
  const exitSourceIds  = [...new Set(boundaryOutgoing.map(c => c.source.id))];

  // 5. Rewire boundary connections BEFORE removing elements
  for (const conn of boundaryIncoming) {
    modeling.reconnectEnd(conn, newShape, getMidpoint(newShape));
  }
  for (const conn of boundaryOutgoing) {
    modeling.reconnectStart(conn, newShape, getMidpoint(newShape));
  }

  // 5. Remove original elements (shapes + internal connections)
  const toRemove = [...internal, ...shapes];
  modeling.removeElements(toRemove);

  // 6. Recreate shapes and connections on the sub-process plane
  canvas.setRootElement(subProcessPlane);

  try {
    const createdShapes = recreateShapesOnPlane(shapeData, subProcessBO, subProcessPlane, canvas);
    recreateConnectionsOnPlane(connectionData, createdShapes, subProcessBO, subProcessPlane, canvas);

    // 7. Create Start/End Events and connect them to entry/exit shapes
    createPlaneStartEndEvents({
      shapeData, createdShapes, entryTargetIds, exitSourceIds,
      subProcessPlane, modeling, elementFactory
    });
  } finally {
    canvas.setRootElement(previousRoot);
  }

  // 10. Fire event to update drill-down overlays
  eventBus.fire('import.render.complete');

  selection.select(newShape);
};


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Create Start and End Events on a sub-process plane and connect them
 * to the entry/exit shapes that were transferred onto that plane.
 */
function createPlaneStartEndEvents(opts) {
  const { shapeData, createdShapes, entryTargetIds, exitSourceIds,
          subProcessPlane, modeling, elementFactory } = opts;

  const planeBbox = computeBoundingBox(
    shapeData.map(d => ({ x: d.x, y: d.y, width: d.width, height: d.height }))
  );
  const planeCenterY = planeBbox.y + planeBbox.height / 2;

  const startEvtShape = elementFactory.createShape({ type: 'bpmn:StartEvent' });
  const startEvt = modeling.createShape(
    startEvtShape, { x: planeBbox.x - 50, y: planeCenterY }, subProcessPlane
  );

  const endEvtShape = elementFactory.createShape({ type: 'bpmn:EndEvent' });
  const endEvt = modeling.createShape(
    endEvtShape, { x: planeBbox.x + planeBbox.width + 50, y: planeCenterY }, subProcessPlane
  );

  connectStartToEntries(startEvt, entryTargetIds, shapeData, createdShapes, modeling);
  connectExitsToEnd(endEvt, exitSourceIds, shapeData, createdShapes, modeling);
}

/** Connect a Start Event to entry shapes (or the leftmost shape as fallback). */
function connectStartToEntries(startEvt, entryTargetIds, shapeData, createdShapes, modeling) {
  if (entryTargetIds.length > 0) {
    for (const id of entryTargetIds) {
      if (createdShapes[id]) {
        modeling.connect(startEvt, createdShapes[id], { type: 'bpmn:SequenceFlow' });
      }
    }
  } else {
    const leftmost = shapeData.reduce((a, b) => (a.x < b.x ? a : b));
    if (createdShapes[leftmost.id]) {
      modeling.connect(startEvt, createdShapes[leftmost.id], { type: 'bpmn:SequenceFlow' });
    }
  }
}

/** Connect exit shapes to an End Event (or the rightmost shape as fallback). */
function connectExitsToEnd(endEvt, exitSourceIds, shapeData, createdShapes, modeling) {
  if (exitSourceIds.length > 0) {
    for (const id of exitSourceIds) {
      if (createdShapes[id]) {
        modeling.connect(createdShapes[id], endEvt, { type: 'bpmn:SequenceFlow' });
      }
    }
  } else {
    const rightmost = shapeData.reduce((a, b) => (a.x + a.width > b.x + b.width ? a : b));
    if (createdShapes[rightmost.id]) {
      modeling.connect(createdShapes[rightmost.id], endEvt, { type: 'bpmn:SequenceFlow' });
    }
  }
}

function computeBoundingBox(shapes) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  for (const shape of shapes) {
    minX = Math.min(minX, shape.x);
    minY = Math.min(minY, shape.y);
    maxX = Math.max(maxX, shape.x + shape.width);
    maxY = Math.max(maxY, shape.y + shape.height);
  }

  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/**
 * Recreate shapes on a sub-process plane from pre-collected shape data.
 */
function recreateShapesOnPlane(shapeData, subProcessBO, plane, canvas) {
  const created = {};

  for (const data of shapeData) {
    const bo = data.businessObject;
    bo.$parent = subProcessBO;
    if (!subProcessBO.flowElements.includes(bo)) {
      subProcessBO.flowElements.push(bo);
    }
    if (!bo.incoming) bo.incoming = [];
    if (!bo.outgoing) bo.outgoing = [];

    attachDiToSubProcessPlane(data.di, canvas, subProcessBO);

    const attrs = {
      id: data.id, type: data.type, businessObject: bo, di: data.di,
      x: data.x, y: data.y, width: data.width, height: data.height,
      incoming: [], outgoing: []
    };
    if (data.isExpanded !== undefined) attrs.isExpanded = data.isExpanded;

    try {
      created[data.id] = canvas.addShape(attrs, plane);
    } catch (err) {
      console.warn('[WrapInSubProcess] Shape creation error:', data.id, err);
    }
  }

  return created;
}

/**
 * Recreate connections on a sub-process plane from pre-collected data.
 */
function recreateConnectionsOnPlane(connectionData, createdShapes, subProcessBO, plane, canvas) {
  for (const data of connectionData) {
    const source = createdShapes[data.sourceId];
    const target = createdShapes[data.targetId];
    if (!source || !target) continue;

    const bo = data.businessObject;
    bo.$parent = subProcessBO;
    if (!subProcessBO.flowElements.includes(bo)) {
      subProcessBO.flowElements.push(bo);
    }
    attachDiToSubProcessPlane(data.di, canvas, subProcessBO);

    try {
      const conn = canvas.addConnection({
        id: data.id, type: data.type, businessObject: bo, di: data.di,
        source, target, waypoints: data.waypoints
      }, plane);
      source.outgoing.push(conn);
      target.incoming.push(conn);
    } catch (err) {
      console.warn('[WrapInSubProcess] Connection creation error:', data.id, err);
    }
  }
}

function getMidpoint(shape) {
  return {
    x: shape.x + shape.width / 2,
    y: shape.y + shape.height / 2
  };
}

/**
 * Connect a Start Event to entry shapes (or leftmost shape as fallback).
 */
function connectStartEvent(entryTargetIds, shapes, startEvent, modeling, elementRegistry) {
  if (entryTargetIds.length > 0) {
    for (const targetId of entryTargetIds) {
      const target = elementRegistry.get(targetId);
      if (target) modeling.connect(startEvent, target, { type: 'bpmn:SequenceFlow' });
    }
  } else {
    const leftmost = shapes.reduce((a, b) => (a.x < b.x ? a : b));
    const target = elementRegistry.get(leftmost.id);
    if (target) modeling.connect(startEvent, target, { type: 'bpmn:SequenceFlow' });
  }
}

/**
 * Connect exit shapes to an End Event (or rightmost shape as fallback).
 */
function connectEndEvent(exitSourceIds, shapes, endEvent, modeling, elementRegistry) {
  if (exitSourceIds.length > 0) {
    for (const sourceId of exitSourceIds) {
      const source = elementRegistry.get(sourceId);
      if (source) modeling.connect(source, endEvent, { type: 'bpmn:SequenceFlow' });
    }
  } else {
    const rightmost = shapes.reduce((a, b) => (a.x + a.width > b.x + b.width ? a : b));
    const source = elementRegistry.get(rightmost.id);
    if (source) modeling.connect(source, endEvent, { type: 'bpmn:SequenceFlow' });
  }
}

/**
 * Connect elements by ID to/from a target shape (direction: 'source' or 'target').
 */
function connectByIds(ids, shape, direction, modeling, elementRegistry) {
  for (const id of ids) {
    const el = elementRegistry.get(id);
    if (!el) continue;
    if (direction === 'source') {
      modeling.connect(el, shape, { type: 'bpmn:SequenceFlow' });
    } else {
      modeling.connect(shape, el, { type: 'bpmn:SequenceFlow' });
    }
  }
}

/**
 * Classify connections relative to the selection into:
 * - internal: both source AND target are in the selection
 * - incoming: target is in selection, source is outside
 * - outgoing: source is in selection, target is outside
 */
function classifyConnections(shapes, selectedIds) {
  const internal = [];
  const incoming = [];
  const outgoing = [];
  const seen = new Set();

  for (const shape of shapes) {
    classifyConnectionList(shape.incoming, selectedIds, seen, internal, incoming, outgoing);
    classifyConnectionList(shape.outgoing, selectedIds, seen, internal, incoming, outgoing);
  }

  return { internal, incoming, outgoing };
}

function classifyConnectionList(conns, selectedIds, seen, internal, incoming, outgoing) {
  for (const conn of (conns || [])) {
    if (seen.has(conn.id)) continue;
    seen.add(conn.id);

    const srcIn = selectedIds.has(conn.source?.id);
    const tgtIn = selectedIds.has(conn.target?.id);

    if (srcIn && tgtIn) {
      internal.push(conn);
    } else if (tgtIn) {
      incoming.push(conn);
    } else if (srcIn) {
      outgoing.push(conn);
    }
  }
}

function allOnSamePlane(elements) {
  if (elements.length === 0) return false;
  const firstParent = elements[0].parent;
  return elements.every(el => el.parent === firstParent);
}

/**
 * Validate whether wrapping the given selection would leave "stranded" nodes.
 *
 * A node is considered stranded when it is NOT selected but ALL of its
 * sequence-flow neighbours (sources + targets) ARE selected.  After wrapping
 * those neighbours into a sub-process the stranded node would sit outside
 * the sub-process connected only to it — likely not the user's intention.
 *
 * Returns an array of { id, name } objects describing the affected nodes.
 * An empty array means the selection is clean.
 */
export function validateWrapSelection(elements) {
  if (!elements || elements.length < 2) return [];

  const selectedIds = new Set(elements.map(e => e.id));
  const candidateMap = collectUnselectedNeighbours(elements, selectedIds);

  const stranded = [];
  for (const [, node] of candidateMap) {
    if (checkAllNeighboursSelected(node, selectedIds)) {
      const bo = node.businessObject;
      stranded.push({ id: node.id, name: bo?.name || node.id });
    }
  }

  return stranded;
}

/** Collect direct unselected neighbours of the selected elements. */
function collectUnselectedNeighbours(elements, selectedIds) {
  const candidateMap = new Map();
  for (const el of elements) {
    for (const conn of (el.incoming || [])) {
      if (conn.source && !selectedIds.has(conn.source.id) && !conn.source.waypoints) {
        candidateMap.set(conn.source.id, conn.source);
      }
    }
    for (const conn of (el.outgoing || [])) {
      if (conn.target && !selectedIds.has(conn.target.id) && !conn.target.waypoints) {
        candidateMap.set(conn.target.id, conn.target);
      }
    }
  }
  return candidateMap;
}

/** True when every sequence-flow neighbour of `node` is inside `selectedIds`. */
function checkAllNeighboursSelected(node, selectedIds) {
  let hasNeighbour = false;
  for (const conn of (node.incoming || [])) {
    if (!conn.source) continue;
    hasNeighbour = true;
    if (!selectedIds.has(conn.source.id)) return false;
  }
  for (const conn of (node.outgoing || [])) {
    if (!conn.target) continue;
    hasNeighbour = true;
    if (!selectedIds.has(conn.target.id)) return false;
  }
  return hasNeighbour; // must have at least one neighbour
}

/**
 * Attach a DI element (BPMNShape or BPMNEdge) to the BPMNPlane
 * that corresponds to the given parent business object.
 */
function attachDiToSubProcessPlane(di, canvas, parentBO) {
  if (!di) return;

  const definitions = getDefinitions(canvas);
  if (!definitions) return;

  // Find the plane for this parent BO
  const diagram = definitions.diagrams?.find(d => d.plane?.bpmnElement === parentBO);
  if (!diagram?.plane) return;

  if (!diagram.plane.planeElement) diagram.plane.planeElement = [];

  di.$parent = diagram.plane;
  if (!diagram.plane.planeElement.includes(di)) {
    diagram.plane.planeElement.push(di);
  }
}

function getDefinitions(canvas) {
  try {
    for (const root of canvas.getRootElements()) {
      const defs = root.businessObject?.$parent;
      if (defs?.$type === 'bpmn:Definitions') return defs;
    }
  } catch (e) {
    console.debug('[WrapInSubProcess] Canvas not ready:', e);
  }
  return null;
}

/**
 * Create a BPMNDiagram + BPMNPlane + canvas root for a collapsed sub-process.
 */
function ensureSubProcessPlane(shape, canvas, bpmnFactory, elementFactory) {
  const planeId = shape.id + '_plane';
  const existing = canvas.getRootElements().find(r => r.id === planeId);
  if (existing) return existing;

  const bo = shape.businessObject;

  const diPlane = bpmnFactory.create('bpmndi:BPMNPlane', { bpmnElement: bo });
  const diDiagram = bpmnFactory.create('bpmndi:BPMNDiagram', { plane: diPlane });
  diPlane.$parent = diDiagram;

  const definitions = getDefinitions(canvas);
  if (definitions) {
    definitions.diagrams.push(diDiagram);
  }

  const planeElement = elementFactory.createRoot({
    id: planeId,
    type: bo.$type,
    di: diPlane,
    businessObject: bo,
    collapsed: true
  });

  canvas.addRootElement(planeElement);
  return planeElement;
}
